import React, { useState } from "react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "@/components/ui-bits";
import { PageHero, Section } from "@/components/PageShell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Field } from "@/pages/Login";

const PATHWAYS = [
  "Submit a diamond", "Become an industry participant", "Technology integration",
  "Laboratory participation", "Retailer access", "International buyer enquiry",
  "Government & institutional enquiry",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", pathway: PATHWAYS[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Enquiry received");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHero
        eyebrow="Contact & Participation"
        title="Separate pathways for every conversation."
        subtitle="Whether you are submitting a diamond, joining as a participant, integrating technology or enquiring as a buyer, start here."
      />
      <Section>
        <div className="grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <div>
              <Eyebrow>Choose a pathway</Eyebrow>
              <div className="mt-6 space-y-2.5">
                {PATHWAYS.map((p) => (
                  <button
                    key={p}
                    data-testid={`pathway-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => set("pathway")(p)}
                    className={`w-full text-left rounded-xl border px-5 py-3.5 text-sm transition-all duration-300 ${
                      form.pathway === p ? "border-bb-gold/50 bg-bb-gold/8 text-bb-white" : "border-white/10 bg-white/[0.02] text-bb-silver/70 hover:border-bb-gold/25"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="glass rounded-2xl p-8">
              {sent ? (
                <div data-testid="contact-success" className="text-center py-16">
                  <h3 className="font-serif text-3xl">Thank you.</h3>
                  <p className="mt-3 text-sm text-bb-silver/65">Our team will be in touch regarding your <span className="text-bb-gold">{form.pathway}</span> enquiry.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <Field label="Name" testid="contact-name" value={form.name} onChange={set("name")} placeholder="Your full name" />
                  <Field label="Email" testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="you@organisation.com" />
                  <Field label="Organisation" testid="contact-org" value={form.organisation} onChange={set("organisation")} placeholder="Company or institution" />
                  <div>
                    <label className="label-eyebrow">Message</label>
                    <textarea
                      data-testid="contact-message"
                      value={form.message}
                      onChange={(e) => set("message")(e.target.value)}
                      rows={5}
                      placeholder={`Regarding: ${form.pathway}`}
                      className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-sm outline-none focus:border-bb-gold/50 transition-colors duration-300 resize-none"
                    />
                  </div>
                  <button data-testid="contact-submit-btn" disabled={loading} className="btn-gold rounded-full w-full py-3.5 text-sm">
                    {loading ? "Sending…" : `Send ${form.pathway} enquiry`}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
